import { supabase } from './supabase';

export type CustomerType = 'new' | 'repeat';

export interface CustomerTypeCounts {
  new: number;
  repeat: number;
  total: number;
}

/**
 * Customer Repeat Tracking Service
 * 
 * Handles the new vs repeat customer classification system.
 * - New customers: repeat = false (0-1 completed bookings)
 * - Repeat customers: repeat = true (2+ completed bookings, permanent)
 */
export class CustomerRepeatTrackingService {
  
  /**
   * Get the customer type for a specific customer
   */
  static async getCustomerType(customerId: string): Promise<CustomerType | null> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase
        .rpc('get_customer_type', { customer_id_param: customerId });

      if (error) {
        console.error('Error getting customer type:', error);
        throw error;
      }

      return data as CustomerType;
    } catch (error) {
      console.error('Error in getCustomerType:', error);
      return null;
    }
  }

  /**
   * Get customer counts by type for a business
   */
  static async getCustomerCountsByType(businessId: string): Promise<CustomerTypeCounts> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase
        .rpc('get_customer_counts_by_type', { business_id_param: businessId });

      if (error) {
        console.error('Error getting customer counts by type:', error);
        throw error;
      }

      // Process the results into a consistent format
      const counts: CustomerTypeCounts = {
        new: 0,
        repeat: 0,
        total: 0
      };

      if (data && Array.isArray(data)) {
        data.forEach((row: { customer_type: string; count: number }) => {
          if (row.customer_type === 'new') {
            counts.new = row.count;
          } else if (row.customer_type === 'repeat') {
            counts.repeat = row.count;
          }
        });
      }

      counts.total = counts.new + counts.repeat;
      return counts;
    } catch (error) {
      console.error('Error in getCustomerCountsByType:', error);
      return {
        new: 0,
        repeat: 0,
        total: 0
      };
    }
  }

  /**
   * Get new customers for a business
   */
  static async getNewCustomers(businessId: string, limit?: number) {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      let query = supabase
        .from('customers')
        .select('*')
        .eq('business_id', businessId)
        .eq('repeat', false)
        .order('created_at', { ascending: false });

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error getting new customers:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getNewCustomers:', error);
      return [];
    }
  }

  /**
   * Get repeat customers for a business
   */
  static async getRepeatCustomers(businessId: string, limit?: number) {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      let query = supabase
        .from('customers')
        .select('*')
        .eq('business_id', businessId)
        .eq('repeat', true)
        .order('created_at', { ascending: false });

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error getting repeat customers:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getRepeatCustomers:', error);
      return [];
    }
  }

  /**
   * Force recalculate repeat status for a specific customer
   * (Useful for testing or manual corrections)
   */
  static async recalculateCustomerRepeatStatus(customerId: string): Promise<boolean> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Get current customer info
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .select('id, repeat')
        .eq('id', customerId)
        .single();

      if (customerError || !customer) {
        console.error('Error getting customer:', customerError);
        return false;
      }

      // Count completed bookings
      const { count, error: countError } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customerId)
        .eq('status', 'completed');

      if (countError) {
        console.error('Error counting bookings:', countError);
        return false;
      }

      const completedBookings = count || 0;
      const shouldBeRepeat = completedBookings >= 2;

      // Update if necessary
      if (customer.repeat !== shouldBeRepeat) {
        const { error: updateError } = await supabase
          .from('customers')
          .update({ repeat: shouldBeRepeat })
          .eq('id', customerId);

        if (updateError) {
          console.error('Error updating customer repeat status:', updateError);
          return false;
        }

        console.log(`Customer ${customerId} repeat status updated: ${customer.repeat} -> ${shouldBeRepeat} (${completedBookings} completed bookings)`);
      }

      return true;
    } catch (error) {
      console.error('Error in recalculateCustomerRepeatStatus:', error);
      return false;
    }
  }

  /**
   * Get customer type percentage breakdown for a business
   */
  static async getCustomerTypePercentages(businessId: string): Promise<{
    newPercentage: number;
    repeatPercentage: number;
  }> {
    const counts = await this.getCustomerCountsByType(businessId);
    
    if (counts.total === 0) {
      return {
        newPercentage: 0,
        repeatPercentage: 0
      };
    }

    return {
      newPercentage: Math.round((counts.new / counts.total) * 100),
      repeatPercentage: Math.round((counts.repeat / counts.total) * 100)
    };
  }
} 